import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActionsSubject, Store } from '@ngrx/store';
import {
  combineLatest, distinctUntilChanged, filter, map, merge, Observable, pairwise, shareReplay, withLatestFrom,
} from 'rxjs';
import { ofType } from '@ngrx/effects';
import { FlowState, MarketingOrder } from '../models';
import {
  AddOrderComplete, ORDERSTATE, SELECTEDORDER, WorkflowActionNext,
} from '../state-mgmt';
import { AmplitudeProperties } from './amplitude.service';

export interface AmplitudeEvent {
  eventType: string;
  properties: AmplitudeProperties;
}

interface OrderStatusSnapshot {
  order: MarketingOrder;
  orderId: string;
  status: string;
}

@Injectable({ providedIn: 'root' })
export class AmplitudeEventsService {
  readonly events$: Observable<AmplitudeEvent>;
  readonly orderContext$: Observable<AmplitudeProperties>;

  constructor() {
    const actions = inject(ActionsSubject);
    const router = inject(Router);
    const store = inject(Store);
    const order$: Observable<MarketingOrder> = store.select(SELECTEDORDER);
    const workflow$: Observable<FlowState> = store.select(ORDERSTATE);

    this.orderContext$ = combineLatest([order$, workflow$]).pipe(
      map(([order, workflow]) => this.getOrderProperties(order, workflow)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const orderStarted$ = actions.pipe(
      ofType(AddOrderComplete),
      map(({ payload }) => ({
        eventType: 'Marketing Order Started',
        properties: this.getOrderProperties(payload),
      })),
    );

    const workflowStepCompleted$ = actions.pipe(
      ofType(WorkflowActionNext),
      filter(({ data }) => !!data?.stepCompleted),
      withLatestFrom(order$, workflow$),
      filter(([, order, workflow]) => !!order && !!workflow?.currentStep),
      map(([, order, workflow]) => ({
        eventType: 'Marketing Order Step Completed',
        properties: {
          ...this.getOrderProperties(order, workflow),
          completedPagePath: router.url,
        },
      })),
    );

    const orderStatusChanged$ = order$.pipe(
      filter((order) => !!order?._id && !!order.status),
      map((order): OrderStatusSnapshot => ({
        order,
        orderId: order._id,
        status: order.status,
      })),
      distinctUntilChanged((previous, current) => (
        previous.orderId === current.orderId && previous.status === current.status
      )),
      pairwise(),
      filter(([previous, current]) => previous.orderId === current.orderId),
      map(([previous, current]) => ({
        eventType: 'Marketing Order Status Changed',
        properties: {
          ...this.getOrderProperties(current.order),
          previousOrderStatus: previous.status,
          newOrderStatus: current.status,
        },
      })),
    );

    this.events$ = merge(orderStarted$, workflowStepCompleted$, orderStatusChanged$);
  }

  private getOrderProperties(order?: MarketingOrder, workflow?: FlowState): AmplitudeProperties {
    if (!order) return {};

    const products = order.selectedPackage?.products || [];
    return {
      marketingOrderId: order._id || '',
      orderStatus: order.status || '',
      orderType: order.__t || '',
      selectedPackageCode: order.selectedPackage?.code || '',
      selectedPackageName: order.selectedPackage?.title || '',
      productCount: products.length,
      productCodes: products.map((product) => product.code).join(','),
      currentWorkflowStepId: workflow?.currentStep?.id || '',
      currentWorkflowStepName: workflow?.currentStep?.displayName || '',
      ...(order.selfService == null ? {} : { selfService: order.selfService }),
    };
  }
}
